
import { Component, Vue, Watch } from "vue-property-decorator";
import { keyCodeFinder } from "@/services/keyCodeService/keyCodeService";
import { KeySet } from "@/services/keyCodeService/keyCode.types";
import { Getter, Mutation } from "vuex-class";
import {
  ApiStorageMap,
  SettingsCardSections,
  SettingsGetters,
  SettingsMutations,
  SettingsTitle
} from "@/components/settings/types";
import { Namespace } from "@/store/types";
import SpatialNavigation from "spatial-navigation-js";
import apiService from "@/services/apiService/apiService";
import { RouteName } from "@/router/types";
import PinChange from "@/components/settings/components/PinChange.vue";
import { LocaleSettingsRightSideMenu } from "@/plugins/i18n/types";
import LanguageSelect from "@/components/settings/components/LanguageSelect.vue";
import storageService from "@/services/storageService/storageService";
import { LocalStorageItems } from "@/helpers/constants";
import StorageService from "@/services/storageService/storageService";
import apiStorageInterface from "@/services/apiStorageInterface";
import store from "@/store";
import { AlertAction, AlertType } from "@/components/alert/types";

@Component({
  components: {
    LanguageSelect,
    PinChange
  }
})
export default class RightSideMenu extends Vue {
  @Mutation(SettingsMutations.setOpenRightSideMenu, {
    namespace: Namespace.Settings
  })
  setOpenRightSideMenu!: (overlayStatus: boolean) => void;

  @Getter(SettingsGetters.getRightSideMenuTitle, {
    namespace: Namespace.Settings
  })
  getRightSideMenuTitle!: SettingsTitle;

  @Getter(SettingsGetters.getActiveCardSection, {
    namespace: Namespace.Settings
  })
  getActiveCardSection?: SettingsCardSections;

  @Getter(SettingsGetters.getActiveCardIndex, {
    namespace: Namespace.Settings
  })
  getActiveCardIndex?: number;

  @Getter(SettingsGetters.getOpenRightSideMenu, {
    namespace: Namespace.Settings
  })
  getOpenRightSideMenu!: boolean;

  widthLocal = "0%";
  settingsCardSections = SettingsCardSections;
  focusedOnce = false;

  alertMsg = "";

  LocaleSettingsRightSideMenu = LocaleSettingsRightSideMenu;

  apiStorageMap: ApiStorageMap = {
    [LocalStorageItems.CustomChannelList]:
      apiStorageInterface.fetchCustomChannelList,
    [LocalStorageItems.SortedCategories]:
      apiStorageInterface.fetchSortedCategories,
    [LocalStorageItems.Promotions]: apiStorageInterface.fetchPromotions,
    [LocalStorageItems.SortedBuffetCategories]:
      apiStorageInterface.fetchSortedBuffetCategories,
    [LocalStorageItems.ProviderData]: apiStorageInterface.fetchProviderData,
    [LocalStorageItems.UserInfo]: apiStorageInterface.fetchUserInfo,
    [LocalStorageItems.AppMenuItems]: apiStorageInterface.fetchAppMenuItems,
    [LocalStorageItems.HomePageWidgets]:
      apiStorageInterface.fetchHomePageWidgets,
    [LocalStorageItems.MoviesByGenre]: apiStorageInterface.fetchMoviesByGenre,
    [LocalStorageItems.TvShowsByGenre]: apiStorageInterface.fetchTvShowsByGenre,
    [LocalStorageItems.FavoriteMovies]: apiStorageInterface.fetchFavoriteMovies,
    [LocalStorageItems.FavoriteTvShows]:
      apiStorageInterface.fetchFavoriteTvShows
  };

  loading = false;

  mounted(): void {
    SpatialNavigation.focus(".settings-menu-right");
    SpatialNavigation.pause();
    setTimeout(() => (this.widthLocal = "100%"), 500);

    const menu = document.getElementById("menuOverlayContainer");
    if (menu) {
      menu.addEventListener("click", this.clickHandler);
    }
  }

  async logOut(): Promise<void> {
    try {
      await apiService.logOut();
      this.closeMenu();
      storageService.clear();
      await this.$router.push({ name: RouteName.Login });
    } catch (e) {
      console.log(e.response);
    }
  }

  async restart(): Promise<void> {
    if (!this.loading) {
      const values = Object.values(LocalStorageItems);
      try {
        this.loading = true;
        for (const value of values) {
          if (
            value !== LocalStorageItems.Token &&
            value !== LocalStorageItems.DeviceId &&
            StorageService.hasItem(value)
          ) {
            StorageService.removeItem(value);
            await this.apiStorageMap[value]();
          }
        }
        await store.dispatch(`${Namespace.Alert}/${AlertAction.UpdateAlert}`, {
          show: true,
          message: "Application info updated",
          alertType: AlertType.Info
        });
        this.closeMenu();
        await this.$router.push({ name: RouteName.Home });
      } catch (e) {
        this.loading = false;
        await store.dispatch(`${Namespace.Alert}/${AlertAction.UpdateAlert}`, {
          show: true,
          message: "Could not refresh data, please try again later",
          alertType: AlertType.Danger
        });
      }
    }
  }

  closeMenu(): void {
    this.setOpenRightSideMenu(false);
    SpatialNavigation.resume();
  }

  //close menu after clicking outside of it
  clickHandler = (event: MouseEvent): void => {
    const target = event.target as HTMLElement;
    const menu = document.getElementById("menuContainer");

    if (menu && !menu.contains(target)) {
      this.closeMenu();
    }
  };

  handleMenuActions(event: KeyboardEvent): void {
    switch (keyCodeFinder(event.keyCode)) {
      case KeySet.KEY_DOWN:
        if (
          this.getActiveCardIndex === 0 &&
          this.getActiveCardSection === this.settingsCardSections.Personal
        ) {
          SpatialNavigation.focus("#logOutBtn");
        }
        if (
          this.getActiveCardIndex === 1 &&
          this.getActiveCardSection === this.settingsCardSections.Personal
        ) {
          SpatialNavigation.focus("#reloadOutBtn");
        }
        if (
          this.getActiveCardIndex === 2 &&
          this.getActiveCardSection === this.settingsCardSections.Personal &&
          !this.focusedOnce
        ) {
          const firstItemId = document
            .getElementById("languages")
            ?.querySelectorAll(":scope > .menu-element-container")[0].id;
          SpatialNavigation.focus("#" + firstItemId);
          this.focusedOnce = true;
        }
        if (
          this.getActiveCardIndex === 3 &&
          this.getActiveCardSection === this.settingsCardSections.Personal &&
          !this.focusedOnce
        ) {
          SpatialNavigation.focus("#oldPin");
          this.focusedOnce = true;
        }
        break;
      case KeySet.KEY_BACK:
        if (!this.focusedOnce) {
          this.closeMenu();
        }
        break;
    }
  }

  @Watch("getAlertMsg")
  handleAlertChange(newVal: string): void {
    if (newVal !== "") {
      this.alertMsg = newVal;
    }
  }
  beforeDestroy(): void {
    const menu = document.getElementById("menuContainer");
    if (menu) {
      document.removeEventListener("click", this.clickHandler);
    }
  }
}
